import axios from 'axios';
import Micromodal from 'micromodal';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import { getModalOption, Setting, Subscription } from '../App';
import { CUSTOMER_API_URL } from '../constants';

type Image = {
  id: string;
  width: number;
  height: number;
  originalSrc: string;
  transformedSrc: string;
};

type Variant = {
  id: string;
  displayName: string;
  title: string;
  sku: string;
  price: string;
  image?: Image;
  requiresShipping: boolean;
  availableForSale: boolean;
};

type ProductWithImage = {
  productId: string;
  title: string;
  images: Image[];
  variants: Variant[];
  onlineStorePreviewUrl: string;
  isSetItem?: boolean;
};

type Plan = {
  billingPolicyInterval: string;
  billingPolicyIntervalCount: number;
  billingPolicyMaxCycles: number | null;
  billingPolicyMinCycles: number | null;
  firstPricingPolicyAdjustmentType: string | null;
  firstPricingPolicyAdjustmentValue: number | null;
  name: string;
  planId: string;
};

type PlanGroupProduct = {
  merchantCode: string;
  name: string;
  planGroupId: string;
  plans: Plan[];
  products?: ProductWithImage[];
};

type PlanPair = {
  lineId: string;
  planId?: string;
  name?: string;
};

export const useSubscriptionLineEditVariant = (setting: Setting) => {
  const [subscription, setSubscription] = useState<Subscription>();
  const [productDetail, setProductDetail] = useState<PlanGroupProduct>();
  const [planGroups, setPlanGroups] = useState<PlanGroupProduct[]>([]);
  const [subscriptionLinePlan, setSubscriptionLinePlan] = useState<PlanPair[]>([]);
  const [variant, setVariant] = useState<Variant>();
  const [quantity, setQuantify] = useState<number>(1);
  const { id, productId, lineId } = useParams<{ id: string; productId: string; lineId: string }>();
  const reactRouterHistory = useHistory();
  const [isProc, setIsProc] = useState<boolean>(false);

  useEffect(() => {
    Promise.all([loadSubscription(), loadProductDetail(), loadSubscriptionLinePlan()]);

    // MicroModal の初期化
    Micromodal.init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      if (!subscription || !planGroups) return;

      const planGroup = productFilter(planGroups);
      setProductDetail(planGroup);

      if (planGroup) {
        const product = planGroup.products[0];
        // バリエーションが設定されていない場合に、画像をProductに設定されている画像に置き換える
        if (product.variants.length === 1 && product.variants[0].title === 'Default Title' && !product.variants[0].image) {
          setVariant({
            ...product.variants[0],
            image: product.images.length > 0 ? product.images[0] : undefined,
          });
        } else {
          setVariant(product.variants[0]);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription, planGroups]);

  useLayoutEffect(() => {
    const height = document.querySelector('body')!.scrollHeight + 100; // buffer;
    setting.resize(height);
    setting.monitorScroll();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDetail]);

  const loadSubscription = async () => {
    return new Promise((resolve, reject) => {
      axios
        .post(
          `${CUSTOMER_API_URL}?action=getSubscription`,
          {
            subscriptionId: id,
          },
          {
            headers: { Authorization: setting.token },
          },
        )
        .then((response) => {
          setSubscription({ ...response.data, disabled: false });
          resolve(true);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  const loadProductDetail = async () => {
    return new Promise((resolve, reject) => {
      axios
        .post(
          `${CUSTOMER_API_URL}?action=getProductVariant`,
          {
            productId: productId,
          },
          {
            headers: { Authorization: setting.token },
          },
        )
        .then((response) => {
          setPlanGroups(response.data);
          resolve(true);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  const loadSubscriptionLinePlan = async () => {
    return new Promise((resolve, reject) => {
      axios
        .post(
          `${CUSTOMER_API_URL}?action=getSubscriptionLinePlan`,
          {
            subscriptionId: id,
          },
          {
            headers: { Authorization: setting.token },
          },
        )
        .then((response) => {
          setSubscriptionLinePlan(response.data);
          resolve(true);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  const loadSubscriptionLinePlanForOnHandleAddItem = async ({ id }: { id: string }) => {
    return new Promise((resolve, reject) => {
      axios
        .post(
          `${CUSTOMER_API_URL}?action=getSubscriptionLinePlan`,
          {
            subscriptionId: id,
          },
          {
            headers: { Authorization: setting.token },
          },
        )
        .then((response) => {
          setSubscriptionLinePlan(response.data);
          resolve(true);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  const productFilter = (data: PlanGroupProduct[]) => {
    if (!subscription) {
      return undefined;
    }

    const excludeVariants = subscription!.subscriptionLines.map((line) => line.variantId);

    const planGroups = data.map((planGroup) => {
      const plans = planGroup.plans.filter((plan) => {
        return (
          plan.billingPolicyInterval === subscription!.billingPolicyInterval &&
          plan.billingPolicyIntervalCount === subscription!.billingPolicyIntervalCount
        );
      });

      const products = planGroup
        .products!.map((product) => {
          const variants = product.variants.filter((variant) => {
            if (excludeVariants.includes(variant.id)) return false;

            if (!subscription.deliveryCountryCode && variant.requiresShipping) return false;

            return true;
          });

          return {
            ...product,
            variants: variants,
          };
        })
        .filter((product) => product.variants.length);

      return {
        ...planGroup,
        plans: plans,
        products: products,
      };
    });

    return planGroups.filter((planGroup) => {
      return planGroup.plans.length && planGroup.products.length;
    })[0];
  };

  const onChangeVariant = (e: any) => {
    setVariant(productDetail!.products![0].variants.find((variant) => variant.id === e.target.value));
  };

  const onChangeQuantify = (e: any) => {
    setQuantify(e.target.value);
  };

  const onHandleAddItem = async ({
    id,
    planId,
    planName,
    subscriptionLineOnlineStorePreviewUrl,
    productId,
    variantId,
    variantImage,
    title,
    variantTitle,
    quantity,
    variantPrice,
    variantSku,
  }: {
    id: string;
    planId: string;
    planName: string;
    subscriptionLineOnlineStorePreviewUrl: string;
    productId: string;
    variantId: string;
    variantImage: string;
    title: string;
    variantTitle: string;
    variantPrice: string;
    quantity: string;
    variantSku: string;
  }) => {
    try {
      await axios.post(
        `${CUSTOMER_API_URL}?action=addSubscriptionLine`,
        {
          subscriptionId: id,
          planId: planId,
          planName: planName,
          subscriptionLine: {
            onlineStorePreviewUrl: subscriptionLineOnlineStorePreviewUrl,
            productId: productId,
            variantId: variantId,
            variantImage: variantImage,
            title: title,
            variantTitle: variantTitle,
            quantity: Number(quantity),
            currentPriceAmount: variantPrice,
            currentPriceCurrencyCode: 'JPY',
            lineDiscountedPriceAmount: variantPrice,
            lineDiscountedPriceCurrencyCode: 'JPY',
            sku: variantSku,
            lineId: null,
          },
        },
        {
          headers: { Authorization: setting.token },
        },
      );
    } catch (err) {
      console.log('err for adding item', err);
      Micromodal.show('modal-error', getModalOption(setting));
      return;
    }

    Micromodal.show('modal-complete', getModalOption(setting));
  };

  const onSelectItem = async () => {
    if (!variant || !productDetail || !subscriptionLinePlan) {
      return;
    }

    if (subscription!.shop.showStockAlert && !variant!.availableForSale) {
      Micromodal.show('modal-stock-error');
      return;
    }

    setIsProc(true);

    try {
      const planPair =
        lineId === 'new' ? undefined : subscriptionLinePlan!.find((pair) => pair.lineId === decodeURIComponent(lineId));
      const planId = planPair ? planPair!.planId : productDetail.plans[0].planId;
      const planName = planPair ? planPair!.name : productDetail.plans[0].name;

      if (lineId === 'new') {
        // 商品の追加
        await axios.post(
          `${CUSTOMER_API_URL}?action=addSubscriptionLine`,
          {
            subscriptionId: id,
            planId: planId,
            planName: planName,
            subscriptionLine: {
              onlineStorePreviewUrl: productDetail!.products![0].onlineStorePreviewUrl,
              productId: productId,
              variantId: variant!.id,
              variantImage: variant!.image ? variant!.image!.transformedSrc : null,
              title: productDetail!.products![0].title,
              variantTitle: variant!.title,
              quantity: Number(quantity),
              currentPriceAmount: variant!.price,
              currentPriceCurrencyCode: 'JPY',
              lineDiscountedPriceAmount: variant!.price,
              lineDiscountedPriceCurrencyCode: 'JPY',
              sku: variant!.sku,
              lineId: lineId !== 'new' ? lineId : null,
            },
          },
          {
            headers: { Authorization: setting.token },
          },
        );
      } else {
        // 商品の変更
        await axios.post(
          `${CUSTOMER_API_URL}?action=changeSubscriptionLine`,
          {
            subscriptionId: id,
            variantId: variant!.id,
            planId,
            lineId,
            quantity,
          },
          {
            headers: { Authorization: setting.token },
          },
        );
      }
    } catch (e) {
      Micromodal.show('modal-error', getModalOption(setting));
      return;
    }

    Micromodal.show('modal-complete', getModalOption(setting));

    setIsProc(false);
  };

  const onClickCompleteAction = () => {
    reactRouterHistory.push(`/subscriptions/${id}`);
  };

  return {
    subscription,
    productDetail,
    lineId,
    variant,
    quantity,
    onChangeVariant,
    onChangeQuantify,
    onSelectItem,
    onClickCompleteAction,
    isProc,
    onHandleAddItem,
    loadSubscriptionLinePlanForOnHandleAddItem,
  };
};
